"use client";

import { useMemo, useRef, type RefObject } from "react";
import styles from "./animated-agents-section.module.css";
import { SharedCursor } from "@/components/boardblend/shared-cursor";
import {
  useCursorPaths,
  type CursorPath,
} from "@/components/boardblend/use-cursor-path";
import type { ParticipantColor } from "@/lib/participant-colors";

const designPath: CursorPath = {
  points: [
    { x: 18, y: 26 },
    { x: 43, y: 57 },
    { x: 58, y: 31 },
  ],
  controls: [
    [
      { x: 24, y: 25 },
      { x: 38, y: 48 },
    ],
    [
      { x: 49, y: 62 },
      { x: 57, y: 45 },
    ],
    [
      { x: 49, y: 25 },
      { x: 27, y: 20 },
    ],
  ],
};

const engineeringPath: CursorPath = {
  points: [
    { x: 70, y: 14 },
    { x: 58, y: 67 },
    { x: 32, y: 37 },
  ],
  controls: [
    [
      { x: 78, y: 28 },
      { x: 70, y: 58 },
    ],
    [
      { x: 49, y: 72 },
      { x: 37, y: 53 },
    ],
    [
      { x: 39, y: 25 },
      { x: 58, y: 12 },
    ],
  ],
};

const productPath: CursorPath = {
  points: [
    { x: 70, y: 72 },
    { x: 43, y: 22 },
    { x: 24, y: 65 },
  ],
  controls: [
    [
      { x: 66, y: 50 },
      { x: 55, y: 28 },
    ],
    [
      { x: 30, y: 22 },
      { x: 24, y: 48 },
    ],
    [
      { x: 39, y: 73 },
      { x: 57, y: 80 },
    ],
  ],
};

const linePatterns = [
  ["1.15rem", "1.85rem", "0.65rem"],
  ["2.35rem", "0.9rem", "0.45rem"],
  ["2.8rem", "0.75rem"],
  ["0.65rem", "0.8rem", "2.2rem"],
  ["1.6rem", "0.65rem", "1.35rem"],
  ["2.5rem", "0.6rem", "0.5rem"],
  ["0.9rem", "2rem", "0.7rem"],
  ["1.9rem", "1.45rem"],
  ["1.3rem", "0.8rem", "1.5rem"],
  ["2.3rem", "1.1rem"],
];

const compactLinePatterns = [
  ["1.15rem", "0.65rem"],
  ["1.85rem", "0.45rem"],
  ["0.9rem", "1.15rem"],
  ["0.65rem", "0.8rem"],
  ["1.6rem", "0.65rem"],
  ["0.9rem", "0.5rem"],
  ["1.3rem", "0.7rem"],
];

function PlaceholderLines({
  compact = false,
  count = 3,
}: {
  compact?: boolean;
  count?: number;
}) {
  const patterns = compact ? compactLinePatterns : linePatterns;

  return (
    <span className={styles.lines} aria-hidden="true">
      {patterns.slice(0, count).map((pattern, rowIndex) => (
        <span className={styles.line} key={rowIndex}>
          {pattern.map((width, segmentIndex) => (
            <i key={segmentIndex} style={{ width }} />
          ))}
        </span>
      ))}
    </span>
  );
}

function CanvasBlock({
  className,
  compact,
  lineCount,
}: {
  className?: string;
  compact?: boolean;
  lineCount?: number;
}) {
  return (
    <div className={`${styles.canvasBlock} ${className ?? ""}`}>
      <PlaceholderLines compact={compact} count={lineCount} />
    </div>
  );
}

function CollaborationMarker({
  className,
  color,
  markerRef,
  profession,
  thoughts,
}: {
  className: string;
  color: ParticipantColor;
  markerRef: RefObject<HTMLDivElement>;
  profession: string;
  thoughts: [string, string, string];
}) {
  return (
    <div
      className={`${styles.marker} ${className} participant-${color}`}
      ref={markerRef}
      aria-hidden="true"
    >
      <SharedCursor
        color={color}
        name={profession}
        offsetX={0}
        offsetY={0}
        x={0}
        y={0}
      />
      <span className={styles.thoughtCard}>
        {thoughts.map((thought, index) => (
          <span
            className={styles.thought}
            data-thought={index + 1}
            key={thought}
          >
            {thought}
          </span>
        ))}
      </span>
    </div>
  );
}

export function AnimatedAgentsSection() {
  const designRef = useRef<HTMLDivElement>(null);
  const engineeringRef = useRef<HTMLDivElement>(null);
  const productRef = useRef<HTMLDivElement>(null);
  const cursorActors = useMemo(
    () => [
      {
        markerRef: designRef,
        path: designPath,
        initialDelay: 0,
        moveDuration: 1_250,
        cardDuration: 1_650,
        cardGap: 650,
        restDuration: 500,
      },
      {
        markerRef: engineeringRef,
        path: engineeringPath,
        initialDelay: 850,
        moveDuration: 1_450,
        cardDuration: 1_850,
        cardGap: 800,
        restDuration: 700,
      },
      {
        markerRef: productRef,
        path: productPath,
        initialDelay: 1_600,
        moveDuration: 1_100,
        cardDuration: 1_500,
        cardGap: 550,
        restDuration: 900,
      },
    ],
    [],
  );
  useCursorPaths(cursorActors);

  return (
    <section
      className={styles.section}
      aria-labelledby="agents-heading"
    >
      <div className={styles.content}>
        <div className={styles.copy}>
          <h2 id="agents-heading">Shared context means better decisions</h2>
          <p>
            Boardblend brings each teammate’s thinking into one shared canvas,
            enhanced by the AI agent they already use and the preferences,
            tools, and past work it already knows. Their ideas and perspectives
            build on one another instead of remaining siloed, leading to deeper,
            more relevant thinking and a visual plan that captures the team’s
            best ideas.
          </p>
        </div>

        <div
          className={styles.illustration}
          role="img"
          aria-label="Design, engineering, and product perspectives contributing to one shared visual plan"
        >
          <div className={styles.canvas}>
            <div className={styles.canvasHeading}>
              <span className={styles.canvasTitleLine} />
              <span className={styles.canvasSubtitleLine}>
                <i />
                <i />
                <i />
                <i />
              </span>
            </div>
            <div className={styles.canvasLayout}>
              <CanvasBlock className={styles.primaryBlock} lineCount={10} />
              <div className={styles.secondaryBlocks}>
                <CanvasBlock compact lineCount={4} />
                <CanvasBlock compact lineCount={4} />
              </div>
            </div>
          </div>

          <CollaborationMarker
            className={styles.markerBlue}
            color="blue"
            markerRef={designRef}
            profession="Design"
            thoughts={[
              "The primary path should be clearer.",
              "These steps feel like one interaction.",
              "This needs a stronger visual hierarchy.",
            ]}
          />
          <CollaborationMarker
            className={styles.markerOrange}
            color="orange"
            markerRef={engineeringRef}
            profession="Engineering"
            thoughts={[
              "This dependency could slow us down.",
              "We can reuse the existing system here.",
              "This needs a fallback state.",
            ]}
          />
          <CollaborationMarker
            className={styles.markerGreen}
            color="green"
            markerRef={productRef}
            profession="Product"
            thoughts={[
              "This is the core user outcome.",
              "We should validate this assumption first.",
              "Start with the smallest complete workflow.",
            ]}
          />
        </div>
      </div>
    </section>
  );
}
